import { planningLayersBase, planningLayersPoly, planningLayersPoint } from "./planning/planningLayerDefs";
import { cultureLayersBase, cultureLayersPoly, cultureLayersPoint } from "./culture/cultureLayerDefs";
import { environmentLayersBase, environmentLayersPoly, environmentLayersPoint } from "./environment/environmentLayerDefs";
import { hsdsLayersBase, hsdsLayersPoly, hsdsLayersPoly2, hsdsLayersPoint } from "./hsds/hsdsLayerDefs.jsx";

export const SELECTED_FOCUS_AREA_OPACITY = 0.2;
export const GREYED_OUT_FOCUS_AREA_OPACITY = 0.2;

export const groupingLabels = {
  opportunity: "Opportunity",
  boundaries: "Boundaries",
  "culture-infrastructure": "Infrastructure",
  "hsds-experian-retail-locations": "Local Data Company Retail Locations",
  "hsds-non-retail-poi": "Non-retail Points of Interest"
};

export const groupingInfo = {
  opportunity: {
    shortDescription:
      "Opportunity Areas (OAs) are London’s major source of brownfield land which have significant capacity for development.",
    longDescriptionTitle: "Opportunity Areas Information",
    longDescription:
      "Opportunity Areas (OAs) are London’s major source of brownfield land which have significant capacity for development. Development proposals within OAs should conform with strategic directions for the OA(s).",
  },
  boundaries: {
    shortDescription: "Borough, high street and other boundaries.",
  },
  "culture-infrastructure": {
    shortDescription: "Cultural venues and supporting infrastructure.",
    longDescription:
      "Layers from the Cultural Infrastructure Map, which brings together new research and information that has previously not existed in one place. It plots the location of cultural infrastructure at building level as of 2018. The scale of London's cultural infrastructure means that it is ever changing and evolving and some is difficult to locate. The map provides the best snapshot of information that we have gathered to date and it will continue to evolve, adding new categories and information as it becomes available and as Londoners and organisations add missing cultural infrastructure to it, or inform us about closures.",
    longDescriptionTitle: "Infrastructure Layers Information",
  },
  "hsds-experian-retail-locations": {
    shortDescription: "Local Data Company Business Premises data",
    longDescription: [
      (<><b>Local Data Company business premise information is only available to members of the High Streets Data Service (HSDS).</b> For members the login button is at the bottom right of the sidebar.</>),
      (<>Physical site surveyed data, supplemented by additional retail data. Data last updated in 2023. See <a href="https://data.london.gov.uk/dataset/hsds---business-premises">https://data.london.gov.uk/dataset/hsds---business-premises</a> for more details.</>),
    ],
    longDescriptionTitle: "Local Data Company retail locations",
  },
  "hsds-non-retail-poi": {
                shortDescription: "Non-retail Points of Interest",
            longDescriptionTitle: "Non-retail Points of Interest",
            longDescription: [
                (<><b>Point X Points of Interest data from Landmark is only available to members of the High Streets
                    Data
                    Service
                    (HSDS).</b> For members the login button is at the bottom right of the sidebar.</>),
                (<>Includes detailed records of health, education, community and commerce locations. Generated by
                    aggregating,
                    cleansing and deduplicating data sourced from a range of expert partners including Ordnance Survey
                    digital
                    mapping. See <a
                        href="https://data.london.gov.uk/dataset/hsds-premises-dataset">https://data.london.gov.uk/dataset/hsds-premises-dataset</a> for
                    more details.</>),
            ],

  }
};

const mapOfOrderedLayerDefs = () => {
  const result = new Map()

  const applyLayerOrdering = (def, beforeId) => {
    if (!beforeId) {
      return def
    }

    def.layer.beforeId = beforeId
    def.overLayers?.forEach((l) => l.beforeId = beforeId)

    return def
  }
 
  const pushLayerDefs = (defs, beforeId='') => {
    for (const name in defs) {
      const def = applyLayerOrdering(defs[name], beforeId)
      result.set(name, def)
    }
  }

  // Base
  pushLayerDefs(planningLayersBase, 'not-shown/ref-layer-to-insert-before/base')
  pushLayerDefs(cultureLayersBase, 'not-shown/ref-layer-to-insert-before/base')
  pushLayerDefs(environmentLayersBase, 'not-shown/ref-layer-to-insert-before/base')
  pushLayerDefs(hsdsLayersBase, 'not-shown/ref-layer-to-insert-before/base')

  // Poly
  pushLayerDefs(planningLayersPoly, 'not-shown/ref-layer-to-insert-before/poly')
  pushLayerDefs(cultureLayersPoly, 'not-shown/ref-layer-to-insert-before/poly')
  pushLayerDefs(environmentLayersPoly, 'not-shown/ref-layer-to-insert-before/poly')
  pushLayerDefs(hsdsLayersPoly, 'not-shown/ref-layer-to-insert-before/poly')

  // Poly2
  pushLayerDefs(hsdsLayersPoly2, 'not-shown/ref-layer-to-insert-before/poly2')

  // Point
  pushLayerDefs(planningLayersPoint, 'not-shown/ref-layer-to-insert-before/point')
  pushLayerDefs(cultureLayersPoint, 'not-shown/ref-layer-to-insert-before/point')
  pushLayerDefs(environmentLayersPoint, 'not-shown/ref-layer-to-insert-before/point')
  pushLayerDefs(hsdsLayersPoint, 'not-shown/ref-layer-to-insert-before/point')

  return result
}

const toLayerDefsObject = (map) => {
  const result = {}

  for (const item of map.entries()) {
    result[item[0]] = item[1]
  }

  return result
}

export const layerDefsMap = mapOfOrderedLayerDefs()
export const layerDefs = toLayerDefsObject(layerDefsMap)